import { z } from 'zod'

import { connectionStateSchema } from './connection'
import { alertEventSchema, liveSampleSchema } from './monitoring'
import { transferItemSchema } from './transfers'

/**
 * Everything the backend pushes over `/api/events`. A discriminated union means the frontend
 * parses one schema and gets an exhaustive switch for free.
 */
export const serverEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('connection.state'),
    state: connectionStateSchema,
  }),
  z.object({
    type: z.literal('server.changed'),
    /** Null when a server was deleted and the list simply has to be refetched. */
    serverId: z.string().nullable(),
  }),
  z.object({
    type: z.literal('vault.locked'),
    reason: z.enum(['manual', 'timeout']),
  }),
  /** One item of the transfer queue changed; the queue lives in the backend, not in the tab. */
  z.object({
    type: z.literal('transfer.changed'),
    item: transferItemSchema,
  }),
  /** One poll of one server, for the live tiles (§5.8). */
  z.object({
    type: z.literal('metrics.sample'),
    sample: liveSampleSchema,
  }),
  /** An alarm went off or cleared; the UI shows it without waiting for a refetch. */
  z.object({
    type: z.literal('alert.changed'),
    event: alertEventSchema,
    resolved: z.boolean(),
  }),
  /** Sent once right after the socket opens, so the client knows it is live. */
  z.object({
    type: z.literal('hello'),
    at: z.iso.datetime(),
  }),
])
export type ServerEvent = z.infer<typeof serverEventSchema>
